import React, { createContext, useContext } from 'react';
import type { Project } from '../../stores/ProjectsStore';
import type { PublicProject } from '../../stores/KanbanTaskStores';

/**
 * The project a route is about, resolved once by whoever owns the route (`ProjectContainerLayout`,
 * `ProjectRouteProvider` or `AnonymousPage`) and read by the tabs and pages below it.
 */
export type CurrentProject = {
  project: Project | undefined;
  /** `String(project.id)`; on the public board it comes from the share-link token before the project itself arrives. */
  projectId: string | undefined;
  isLoading: boolean;
  isAnonymous: boolean;
  /** Re-reads the project from the server (`getBySlug(slug, true)`); the settings page calls it after a member change. */
  reload: () => Promise<Project | undefined>;
};

const ProjectContext = createContext<CurrentProject | null>(null);

export const ProjectProvider: React.FC<{ value: CurrentProject; children: React.ReactNode }> = ({ value, children }) => (
  <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>
);

export function useCurrentProject(): CurrentProject {
  const value = useContext(ProjectContext);
  if (value === null) {
    throw new Error('useCurrentProject() must be used inside a <ProjectProvider>');
  }
  return value;
}

/**
 * The public board knows four fields of its project (GET api/OpenProjectApi/project). Every reader
 * of the rest already guards (`project?.members || []`, `project.title || project.name`), so the
 * remaining members are the empty values those readers expect.
 */
export function projectFromPublic(dto: PublicProject): Project {
  const partial: Partial<Project> = {
    id: String(dto.id),
    title: dto.name,
    name: dto.name,
    slug: dto.slug,
    companyId: dto.companyId ?? 0,
    isPublish: true,
    isActive: true,
    description: '',
    userProjects: [],
    members: [],
    developers: [],
    projectSkills: [],
  };
  return partial as Project;
}
